import { useEffect } from 'react';
import { useRouter } from 'next/router';

import ALink from '~/components/features/custom-link';

import CartMenu from './cart-menu';
import MainMenu from './main-menu';
import SearchBox from './search-box';
import MobileMenu from './mobile-menu';
import ProfileMenu from './profile-menu';
import ProgramAccess from './program-access';
import BrowseCategories from './browse-categories';
import MobileSearch from './mobile-search';
import DashboardAccess from './dashboard-access';

import { headerBorderRemoveList } from '~/utils/data/menu';
import { useProgram, useAuth } from '~/hooks';
import { isStoreReadOnly } from '~/utils/programAccess';

// Header ÚNICO y responsive (por CSS) para desktop y mobile, basado en el
// original Riode (demo-market2). Adaptado a lala: logo del programa, WhatsApp,
// puntos (moneda del programa), cart real (CartContext) y menú de Perfil con
// nombre + puntos. Pendiente reintroducir: resultados y categorías de BD.
// Estilos del look (blanco+azul), logo y perfil responsive: styles/theme-riode.css.
export default function Header() {
  const router = useRouter();
  const { program } = useProgram();
  const { availablePoints, loadingPoints } = useAuth();

  useEffect(() => {
    let header = document.querySelector('header');
    if (header) {
      if (
        headerBorderRemoveList.includes(router.pathname) &&
        header.classList.contains('header-border')
      )
        header.classList.remove('header-border');
      else if (!headerBorderRemoveList.includes(router.pathname))
        document.querySelector('header')?.classList.add('header-border');
    }
  }, [router.pathname]);

  const showMobileMenu = () => {
    document.querySelector('body')?.classList.add('mmenu-active');
  };

  // Al hacer clic en los puntos (compacto en mobile o caja completa en desktop)
  // llevamos al estado de cuenta del participante.
  const accountStatementHref = {
    pathname: '/pages/account',
    query: { tab: 'account-statement' },
  };

  return (
    <>
      <header className="header">
        <div className="header-middle">
          <div className="container">
            <div className="header-left mr-4">
              <ALink href="#" className="mobile-menu-toggle" onClick={showMobileMenu}>
                <i className="d-icon-bars2"></i>
              </ALink>

              {/* Logo del programa (data dinámica de lala). El sizing responsive
                lo maneja `.header .header-logo` (custom.scss): altura consistente
                vía clamp, width auto + object-fit contain, así logos de cualquier
                tamaño se ven uniformes y no rompen el diseño. Fallback al logo
                estático si el programa no tiene logo. */}
              <ALink href="/" className="logo">
                <img
                  src={program?.logo || '/images/home/logo.png'}
                  alt={program?.name || 'logo'}
                  className="header-logo"
                />
              </ALink>

              <SearchBox />
            </div>

            <div className="header-right">
              {/* Puntos en versión COMPACTA (solo mobile < lg): ícono + número,
                sin etiqueta, para que quepan junto a los íconos. En desktop se
                usa la caja completa (.points-box) de más abajo. */}
              <ALink
                href={accountStatementHref}
                className="points-compact"
                title={`Mis ${program?.coinName}`}
              >
                <i className="fas fa-coins"></i>
                <span>{loadingPoints ? '...' : availablePoints}</span>
              </ALink>

              {/* Buscador mobile: lupa que abre un modal (solo < lg). */}
              <MobileSearch />

              {/* Acceso SSO directo: texto en desktop e icono compacto en mobile. */}
              <DashboardAccess />

              {/* WhatsApp: "Escríbenos" + teléfono del programa -> abre WhatsApp
                (593 = código país; supportPhone viene sin código). */}
              <a
                href={`https://api.whatsapp.com/send?phone=593${
                  program?.supportPhone || ''
                }&text=${encodeURIComponent(
                  `Hola! Soy participante del programa ${
                    program?.name || ''
                  } y tengo una consulta.`,
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="icon-box icon-box-side"
              >
                <div className="icon-box-icon mr-lg-2 mr-0">
                  <i className="fab fa-whatsapp"></i>
                </div>
                <div className="icon-box-content d-lg-show">
                  <h4 className="icon-box-title text-dark text-normal">Escríbenos</h4>
                  <p>{program?.supportPhone}</p>
                </div>
              </a>

              <span className="divider points-box mr-4"></span>

              {/* Puntos disponibles del programa, en su moneda (coinName).
                En pantallas chicas se oculta toda la caja (clase points-box):
                los puntos quedan disponibles en el menú de Perfil. */}
              <ALink href={accountStatementHref} className="icon-box icon-box-side points-box">
                <div className="icon-box-icon mr-lg-2 mr-0">
                  <i className="fas fa-coins"></i>
                </div>
                <div className="icon-box-content d-lg-show">
                  <h4 className="icon-box-title text-dark text-normal">Mis {program?.coinName}</h4>
                  <p>{loadingPoints ? 'Cargando...' : availablePoints}</p>
                </div>
              </ALink>

              <span className="divider"></span>

              {!isStoreReadOnly(program?.id) && <CartMenu />}

              <span className="divider"></span>

              {/* Perfil (cuenta del usuario): nombre + menú flotante. */}
              <ProfileMenu />
            </div>
          </div>
        </div>

        <div className="header-bottom sticky-header fix-top sticky-content d-lg-show">
          <div className="container">
            <div className="header-left">
              {/* Categorías reales del programa (TanStack Query). */}
              <BrowseCategories />

              <MainMenu />
            </div>
            <div className="header-right">
              {/* Accesos rápidos según el programa (reemplazan a los mock
                "Special Offers / Buy Riode" del template). */}
              <ProgramAccess />
            </div>
          </div>
        </div>
      </header>

      <MobileMenu />
    </>
  );
}
